/**
 * PlayArea component manages the game room UI, including active games, waiting states, and player management.
 * Handles game state display, turn information, and coordinates between game components and room actions.
 */
import { useSignals } from '@preact/signals-react/runtime'
import React, { useEffect, useRef, useState } from 'react'
import { PlayAreaUI } from '../PlayAreaUI/PlayAreaUI'
import { GameContainer } from '../GameContainer/GameContainer'
import { WaitingForPlayers } from '../WaitingForPlayers/WaitingForPlayers'
import { NewGameWizard } from '../../newGame/NewGameWizard'

import { useTheme } from '../../../contexts/ThemeContext'
import { StartGameSetupDialog } from '../StartGameSetupDialog/StartGameSetupDialog'
import { GameOptionsDialog } from '../GameOptionsDialog/GameOptionsDialog'
import { useWebContainer } from '../../providers/DepProvider/DepProvider'

interface PlayAreaProps {
  autoStartGameName: string | null
  stateNameFromURL: string | null
}

export function PlayArea({ autoStartGameName, stateNameFromURL }: PlayAreaProps) {
  useSignals()
  const { setGameThemeInput } = useTheme()
  const [isAutoStarting, setIsAutoStarting] = useState(false)
  const hasTriggeredAutoStartRef = useRef<string | null>(null)

  const container = useWebContainer()
  const playAreaVM = container.playAreaVM

  const activeGame = playAreaVM.game.activeGame.value
  const hasActiveGame = playAreaVM.game.hasActiveGame.value
  const roomConnected = container.roomConnection.status.value === 'connected'
  const inRoomMode = container.lobby.roomMode.value === 'room'

  // Auto-start game from URL when we have game ID in path (e.g. /r/test:local/prophecies/ or /r/test:local/prophecies/prophecies-mid)
  useEffect(() => {
    if (!autoStartGameName || !roomConnected || !inRoomMode) return
    if (hasTriggeredAutoStartRef.current === autoStartGameName) return
    const currentGameId = activeGame?.gameId
    if (currentGameId === autoStartGameName) {
      setIsAutoStarting(false)
      return
    }
    hasTriggeredAutoStartRef.current = autoStartGameName
    setIsAutoStarting(true)
    if (stateNameFromURL) {
      container.debugPanelVM.updateInitialStateName(stateNameFromURL)
    }
    const run = async () => {
      // Entering an owned game solo (e.g. a direct /r/<room>/<gameId> link) that needs
      // more players applies Omni mode first, same as picking it from the catalog tile -
      // see wiki/decisions/2026-10-04-use-networked-rooms-for-creator-omni-mode.md.
      const tile = playAreaVM.noActiveGame.games.value.find((g) => g.gameId === autoStartGameName)
      if (tile?.owned && tile.insufficientMates && !container.mode.isOmniMode()) {
        await container.debugPanelVM.applyOmniMode()
      }
      // debugPanelVM.openGame (not roomActions.startGame directly) so a setup that
      // doesn't fit every room mate into the game's slot count - e.g. Omni mode's
      // real-mate-plus-fakes roster - goes through the same setup/auto-seat path as
      // a catalog tile click instead of failing outright (see debugPanelVM.ts).
      await container.debugPanelVM.openGame(autoStartGameName)
    }
    void run()
  }, [autoStartGameName, stateNameFromURL, roomConnected, inRoomMode, activeGame?.gameId, container, playAreaVM])

  // Clear auto-start spinner when the game has loaded. Keep hasTriggeredAutoStartRef so we
  // don't re-trigger when the user closes the game (effect would run with activeGame=null).
  useEffect(() => {
    if (isAutoStarting && hasActiveGame && activeGame?.gameId === autoStartGameName) {
      setIsAutoStarting(false)
      hasTriggeredAutoStartRef.current = autoStartGameName
    }
  }, [isAutoStarting, hasActiveGame, activeGame?.gameId, autoStartGameName])

  // Apply preset state from URL when game loads (TestStateSelector only runs when Presets tab is active)
  const hasAppliedPresetRef = useRef<string | null>(null)
  useEffect(() => {
    if (!stateNameFromURL || !hasActiveGame || !activeGame?.gameId || activeGame.gameId !== autoStartGameName) return
    const key = `${autoStartGameName}:${stateNameFromURL}`
    if (hasAppliedPresetRef.current === key) return
    const position = container.debugPanelVM.positions.value.find(({ name }) => name === stateNameFromURL)
    if (!position) return
    hasAppliedPresetRef.current = key
    void container.debugPanelVM.applyState(position.state).catch(() => {
      hasAppliedPresetRef.current = null
    })
  }, [stateNameFromURL, hasActiveGame, activeGame?.gameId, autoStartGameName, container.debugPanelVM])
  const preGameThemeInput = playAreaVM.theme.preGameThemeInput.value
  const hasActiveGameForTheme = playAreaVM.game.hasActiveGame.value

  // Pre-game / no-game theme only. While a game is active, GameContainer owns
  // gameThemeInput; when the game ends, re-apply preGameThemeInput (usually null)
  // so Merels default colors return.
  React.useLayoutEffect(() => {
    if (hasActiveGameForTheme) return
    setGameThemeInput(preGameThemeInput)
  }, [hasActiveGameForTheme, preGameThemeInput, setGameThemeInput])

  return (
    <>
      <StartGameSetupDialog
        startGameSetup={playAreaVM.startGameSetup}
        isLocalSharedScreen={container.mode.isLocalMode() && container.mode.isSharedScreenMode()}
      />
      {playAreaVM.configureDialog.gameId.value ? (
        <GameOptionsDialog
          open={playAreaVM.configureDialog.isOpen.value}
          onOpenChange={(next) => (next ? null : playAreaVM.configureDialog.close())}
          gameId={playAreaVM.configureDialog.gameId.value as string}
          savedOptions={playAreaVM.configureDialog.savedOptionsForDialog.value}
          roomActions={container.roomActions}
          gameRegistry={container.gameRegistry}
          mode="preGame"
          availablePlayerCounts={playAreaVM.configureDialog.availablePlayerCounts.value}
          defaultPlayerCount={playAreaVM.configureDialog.defaultPlayerCount.value}
        />
      ) : null}
      <PlayAreaUI
        isLoading={playAreaVM.isLoading.value}
        isAutoStarting={isAutoStarting}
        autoStartGameName={autoStartGameName}
        hasActiveGame={playAreaVM.game.hasActiveGame.value}
        isStartingGameSetup={playAreaVM.game.isStartingGameSetup.value}
        noActiveGame={playAreaVM.noActiveGame}
      >
      {activeGame && (
        <div className="relative game-area-content">
          {playAreaVM.waitingForPlayers.isVisible.value && (
            <WaitingForPlayers
              currentPlayerCount={playAreaVM.waitingForPlayers.currentPlayerCount.value}
              requiredPlayerCount={playAreaVM.waitingForPlayers.requiredPlayerCount.value}
              supportsBots={playAreaVM.waitingForPlayers.supportsBots.value}
            />
          )}
          <div
            className="game-board-container h-full w-full flex flex-col"
            data-testid="game-board-container"
          >
            {activeGame.gameId === 'new-game' ? (
              <NewGameWizard />
            ) : (
              <GameContainer
                gameId={activeGame.gameId}
                gameState={activeGame.gameState}
                ctx={activeGame.ctx}
                gameVersion={activeGame.gameVersion}
                playerToMateIds={activeGame.playerToMateIds}
                viewingMateId={playAreaVM.game.viewingMateId.value}
                viewingPlayerIndex={playAreaVM.game.viewingPlayerIndex.value}
                applyMove={playAreaVM.actions.applyMove}
                isManagedRoom={playAreaVM.game.isManagedRoom.value}
                viewerIsHost={Boolean(playAreaVM.game.hostMateId.value)}
                applyHostMove={playAreaVM.actions.applyHostMove}
                getLiveGameState={playAreaVM.game.getLiveGameState}
                error={playAreaVM.game.error.value || undefined}
                gameRegistry={container.gameRegistry}
                mode={container.mode}
                controllerMates={container.localMatesSystem.controllerMates}
                playerAssignmentPreferences={container.matesToPlayers.playerAssignmentPreferences}
                savedOptions={container.roomState.savedOptions.value}
                roomMates={container.roomState.mates.value as any}
                viewMode={container.shellVM.game.viewMode.value}
                hintsEnabled={container.shellVM.game.hintsEnabled.value}
                roomCode={container.roomConnection.roomId.value || undefined}
                crashReporter={container.crashReporter}
              />
            )}
          </div>
        </div>
      )}
      </PlayAreaUI>
    </>
  )
}
